import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaScheduleV2Service, serviceName } from '../service';
import { ListIO, ServiceQueries } from '../types';
import { UseScheduleV2InfiniteQueryEndpointArgs, useScheduleV2InfiniteQuery } from './use-schedule-v2-infinite-query';

const API_RESPONSE_LIMIT = 50;

type ListPageParams = {
  limit: number;
  skip: number;
};
/**
 * A hook that returns an infinite query for the `List` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListInfiniteQuery = <E = unknown, D = ListIO['output']>({
  options,
  request,
  ...args
}: UseScheduleV2InfiniteQueryEndpointArgs<'List', E, D, ListPageParams>) =>
  useScheduleV2InfiniteQuery<'List', E, D, ListPageParams>({
    endpointName: 'List',
    ...args,
    request: ({ pageParam }) => {
      const requestWithPageParams: ListIO['input'] = {
        ...request,
        limit: pageParam?.limit || API_RESPONSE_LIMIT,
        skip: pageParam?.skip || 0,
      };
      return requestWithPageParams;
    },
    options: {
      ...options,
      getNextPageParam: (lastPage, allPages) => {
        if (Number(lastPage.data?.length) < API_RESPONSE_LIMIT) return undefined;

        return {
          limit: API_RESPONSE_LIMIT,
          skip: allPages.length * API_RESPONSE_LIMIT,
        };
      },
    },
  });

/**
 * A hook that returns a function to fetch the query for the `List` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchList = () => {
  const queryClient = useQueryClient();

  const fetchList = async <E = unknown, D = ListIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseScheduleV2InfiniteQueryEndpointArgs<'List', E, D>, 'options'> & {
    options: LimitedSchemaFetchQueryOptions<ListIO, E, D, ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'List'>>>;
  }) => {
    try {
      const response = await queryClient.fetchQuery<
        ListIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'List'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'List'>({
          serviceName,
          endpointName: 'List',
          request,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaScheduleV2Service.List(request, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError ?? ({} as D);
      }
      throw error;
    }
  };

  return fetchList;
};
